import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';

const footerLinks = {
  shop: [
    { name: 'Memory Foam Pillows', path: '/products/memory-foam' },
    { name: 'Down & Feather Pillows', path: '/products/down-feather' },
    { name: 'Cooling Pillows', path: '/products/cooling' },
    { name: 'Orthopedic Pillows', path: '/products/orthopedic' },
    { name: 'Travel Pillows', path: '/products/travel' },
    { name: 'Body Pillows', path: '/products/body' },
  ],
  support: [
    { name: 'Contact Us', path: '/contact' },
    { name: 'FAQs', path: '/faq' },
    { name: 'Shipping & Delivery', path: '/shipping' },
    { name: 'Returns & Exchanges', path: '/returns' },
    { name: 'Track Your Order', path: '/track-order' },
  ],
  company: [
    { name: 'About Us', path: '/about' },
    { name: 'Our Story', path: '/about#story' },
    { name: 'Sleep Blog', path: '/blog' },
    { name: 'Careers', path: '/careers' },
    { name: 'Press', path: '/press' },
  ],
};

function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'primary.main',
        color: 'white',
        pt: 8,
        pb: 4,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand section */}
          <Grid item xs={12} md={4}>
            <Typography
              variant="h5"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 600,
                mb: 2,
              }}
            >
              Sleepout
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, opacity: 0.9, maxWidth: 300 }}>
              Premium pillows designed for the perfect night's sleep. 
              Crafted with care, delivered with love.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton color="inherit" size="small" aria-label="Facebook">
                <FacebookIcon />
              </IconButton>
              <IconButton color="inherit" size="small" aria-label="Instagram">
                <InstagramIcon />
              </IconButton>
              <IconButton color="inherit" size="small" aria-label="Twitter">
                <TwitterIcon />
              </IconButton>
              <IconButton color="inherit" size="small" aria-label="YouTube">
                <YouTubeIcon />
              </IconButton>
            </Box>
          </Grid>

          {/* Links sections */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
              Shop
            </Typography>
            {footerLinks.shop.map((link) => (
              <Link
                key={link.name}
                component={RouterLink}
                to={link.path}
                color="inherit"
                underline="hover"
                display="block"
                sx={{ mb: 1, opacity: 0.9, fontSize: '0.875rem' }}
              >
                {link.name}
              </Link>
            ))}
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
              Support
            </Typography>
            {footerLinks.support.map((link) => (
              <Link
                key={link.name}
                component={RouterLink}
                to={link.path}
                color="inherit"
                underline="hover"
                display="block"
                sx={{ mb: 1, opacity: 0.9, fontSize: '0.875rem' }}
              >
                {link.name}
              </Link>
            ))}
          </Grid>

          <Grid item xs={6} sm={4} md={2}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
              Company
            </Typography>
            {footerLinks.company.map((link) => (
              <Link
                key={link.name}
                component={RouterLink}
                to={link.path}
                color="inherit"
                underline="hover"
                display="block"
                sx={{ mb: 1, opacity: 0.9, fontSize: '0.875rem' }}
              >
                {link.name}
              </Link>
            ))}
          </Grid>

          {/* Newsletter */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
              Stay Updated
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
              Subscribe for exclusive offers and sleep tips.
            </Typography>
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Your email"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'secondary.main' },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'white',
                    '&::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 },
                  },
                }}
              />
              <Button
                variant="contained"
                color="secondary"
                size="small"
                sx={{ fontWeight: 600 }}
              >
                Subscribe
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.2)' }} />

        {/* Bottom section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            © 2026 Sleepout India. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link
              component={RouterLink}
              to="/privacy"
              color="inherit"
              underline="hover"
              sx={{ fontSize: '0.875rem', opacity: 0.8 }}
            >
              Privacy Policy
            </Link>
            <Link
              component={RouterLink}
              to="/terms"
              color="inherit"
              underline="hover"
              sx={{ fontSize: '0.875rem', opacity: 0.8 }}
            >
              Terms of Service
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
